import * as React from 'react';
import { NotificationItem } from '../../../reducers/notifications';
import {
    DrawerField,
    UserChip,
    DrawerDateTime,
    CategoryLabel,
} from './DrawerAtoms';

/**
 * Drawer card system — data-driven, declarative.
 *
 * Each notification's right-side "facts" card is built from a list of
 * FieldConfig entries keyed by event_type.key. To add a new event type,
 * append an entry to CARD_CONFIGS — no new React components needed.
 *
 * Why declarative configs instead of one component per event:
 *   - 10+ event types today, growing to 30+. One file beats 30 small files.
 *   - The shape of every card is identical (label + value rows); only the
 *     fields differ. Configs make the difference explicit and reviewable.
 *   - Adding a new event type means picking which fields it has and what
 *     they read from the payload. That's pure config, not code.
 *
 * Each FieldConfig has:
 *   labelId / labelDefault: i18n key + fallback
 *   render(item, ctx): React node for the value
 *   show(item, ctx): optional predicate; skip the row if false
 */

export interface CardContext {
    locale: string;
}

export interface FieldConfig {
    labelId: string;
    labelDefault: string;
    render: (item: NotificationItem, ctx: CardContext) => React.ReactNode;
    show?: (item: NotificationItem, ctx: CardContext) => boolean;
}

// ── Reusable field builders ──────────────────────────────────────────────
// Same handful of fields appear on most cards (when, by whom, related entity,
// category). Defining them once keeps the per-event configs short and easy
// to scan.

const fieldCreatedAt: FieldConfig = {
    labelId: 'notifications.drawer.createdAt',
    labelDefault: 'Created',
    render: (n, ctx) => <DrawerDateTime iso={n.created_at} locale={ctx.locale} />,
};

const fieldCreatedBy: FieldConfig = {
    labelId: 'notifications.drawer.createdBy',
    labelDefault: 'Created by',
    render: (n) => n.event?.triggered_by
        ? <UserChip user={n.event.triggered_by} />
        : <span>—</span>,
    show: (n) => !!n.event?.triggered_by,
};

const fieldTriggeredBy = (labelId: string, labelDefault: string): FieldConfig => ({
    labelId,
    labelDefault,
    render: (n) => n.event?.triggered_by
        ? <UserChip user={n.event.triggered_by} />
        : <span>—</span>,
    show: (n) => !!n.event?.triggered_by,
});

const fieldFromPayload = (
    payloadKey: string,
    labelId: string,
    labelDefault: string,
): FieldConfig => ({
    labelId,
    labelDefault,
    render: (n) => {
        const value = n.event?.payload?.[payloadKey];
        return value !== null && value !== undefined && value !== ''
            ? <span>{String(value)}</span>
            : <span>—</span>;
    },
    show: (n) => {
        const value = n.event?.payload?.[payloadKey];
        return value !== null && value !== undefined && value !== '';
    },
});

const fieldCategory: FieldConfig = {
    labelId: 'notifications.drawer.category',
    labelDefault: 'Category',
    render: (n) => <CategoryLabel category={n.category} />,
    show: (n) => !!n.category,
};

// ── Per-event-type configs ────────────────────────────────────────────────

const CARD_CONFIGS: Record<string, FieldConfig[]> = {
    'project.created': [
        fieldCreatedAt,
        fieldCreatedBy,
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('project_name', 'notifications.drawer.project', 'Project'),
        fieldCategory,
    ],

    'measure.status_changed': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.changedBy', 'Changed by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('project_name', 'notifications.drawer.project', 'Project'),
        // Payload uses `from` + `to` (the resolved MeasureStatus names) not
        // `from_status` / `to_status`. Field-name mismatch was silently hiding
        // both rows via the `show` predicate — fixed in 2026-05-25 with the
        // measure.status_changed template rewrite.
        fieldFromPayload('from', 'notifications.drawer.fromStatus', 'From'),
        fieldFromPayload('to', 'notifications.drawer.toStatus', 'To'),
        fieldCategory,
    ],

    // Fired when a measurer is assigned to a project. Drawer focuses on who
    // assigned it and the client to visit — those are the two things the
    // measurer wants to know at a glance before clicking through.
    'measure.scheduled': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.assignedBy', 'Assigned by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('project_name', 'notifications.drawer.project', 'Project'),
        fieldFromPayload('scheduled_at', 'notifications.drawer.scheduledFor', 'Scheduled for'),
        fieldCategory,
    ],

    // All three production.process events share the same identity rows
    // (Order # → Client → Project → Product → Phase) and diverge only on
    // the action-specific tail (who completed/blocked/returned + any
    // associated reason). Mirrors the comment drawer pattern from batch 5.
    'production.process.completed': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.completedBy', 'Completed by'),
        fieldFromPayload('order_number', 'notifications.drawer.orderNumber', 'Order #'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('project_name', 'notifications.drawer.project', 'Project'),
        fieldFromPayload('article_name', 'notifications.drawer.product', 'Product'),
        fieldFromPayload('process_name', 'notifications.drawer.phase', 'Phase'),
        fieldCategory,
    ],

    'production.process.blocked': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.blockedBy', 'Blocked by'),
        fieldFromPayload('order_number', 'notifications.drawer.orderNumber', 'Order #'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('project_name', 'notifications.drawer.project', 'Project'),
        fieldFromPayload('article_name', 'notifications.drawer.product', 'Product'),
        fieldFromPayload('process_name', 'notifications.drawer.phase', 'Phase'),
        fieldFromPayload('block_reason', 'notifications.drawer.reason', 'Reason'),
        fieldCategory,
    ],

    'production.process.returned': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.returnedBy', 'Returned by'),
        fieldFromPayload('order_number', 'notifications.drawer.orderNumber', 'Order #'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('project_name', 'notifications.drawer.project', 'Project'),
        fieldFromPayload('article_name', 'notifications.drawer.product', 'Product'),
        fieldFromPayload('process_name', 'notifications.drawer.phase', 'Phase'),
        // `reason` is filled by the dispatcher resolver via the comment-first
        // → translated-category chain (batch 2). Always renders a sensible
        // value in the recipient's locale, or hides if both empty.
        fieldFromPayload('reason', 'notifications.drawer.reason', 'Reason'),
        fieldCategory,
    ],

    // Comment events — surface the FULL context the user asked for:
    // who, where (client/project/product/phase/material), and the comment
    // excerpt itself. The deep-link in action_url lets the "View page"
    // button take them straight to the right popup; the card answers
    // "which conversation is this?" without needing to click through.
    //
    // Material row only renders when the comment was scoped to a material
    // (most production comments are responsibility-level).
    'production.comment.created': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.commentedBy', 'Commented by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('project_name', 'notifications.drawer.project', 'Project'),
        fieldFromPayload('article_name', 'notifications.drawer.product', 'Product'),
        fieldFromPayload('responsibility_name', 'notifications.drawer.phase', 'Phase'),
        fieldFromPayload('material_name', 'notifications.drawer.material', 'Material'),
        fieldFromPayload('order_number', 'notifications.drawer.orderNumber', 'Order #'),
        fieldFromPayload('comment_excerpt', 'notifications.drawer.commentExcerpt', 'Comment'),
        fieldCategory,
    ],

    'production.comment.mentioned': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.commentedBy', 'Commented by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('project_name', 'notifications.drawer.project', 'Project'),
        fieldFromPayload('article_name', 'notifications.drawer.product', 'Product'),
        fieldFromPayload('responsibility_name', 'notifications.drawer.phase', 'Phase'),
        fieldFromPayload('material_name', 'notifications.drawer.material', 'Material'),
        fieldFromPayload('order_number', 'notifications.drawer.orderNumber', 'Order #'),
        fieldFromPayload('comment_excerpt', 'notifications.drawer.commentExcerpt', 'Comment'),
        fieldCategory,
    ],

    'installation.assigned': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.assignedBy', 'Assigned by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('address', 'notifications.drawer.address', 'Address'),
        fieldFromPayload('scheduled_at', 'notifications.drawer.scheduledFor', 'Scheduled for'),
        fieldCategory,
    ],

    'installation.blocked': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.blockedBy', 'Blocked by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('address', 'notifications.drawer.address', 'Address'),
        // installation_block_category_name is injected per-recipient by the
        // dispatcher resolver (resolvePayloadForLocale): given the category_id
        // in the payload, it looks up the Spatie-translatable name in the
        // recipient's active locale. Falls back to sq via translatable.fallback_locale.
        fieldFromPayload('installation_block_category_name', 'notifications.drawer.blockCategory', 'Block category'),
        fieldFromPayload('block_type', 'notifications.drawer.blockType', 'Block type'),
        fieldFromPayload('block_reason', 'notifications.drawer.reason', 'Reason'),
        fieldCategory,
    ],

    // Transport payload (today) carries client_name + driver_user_id only.
    // No `address` (delivery destinations live per TransportItem, may span
    // multiple items per transport) and no `scheduled_at` (Transport has no
    // schedule column yet). Drawer keeps it honest — shows what's actually
    // there; users click through for itemized destinations.
    'transport.assigned': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.assignedBy', 'Assigned by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldCategory,
    ],

    'servicing.created': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.openedBy', 'Opened by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('servicing_type', 'notifications.drawer.serviceType', 'Type'),
        fieldFromPayload('description', 'notifications.drawer.description', 'Description'),
        fieldCategory,
    ],

    'offer.accepted': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.acceptedBy', 'Accepted by'),
        fieldFromPayload('client_name', 'notifications.drawer.client', 'Client'),
        fieldFromPayload('offer_number', 'notifications.drawer.offerNumber', 'Offer #'),
        fieldFromPayload('total', 'notifications.drawer.total', 'Total'),
        fieldCategory,
    ],

    // Generic cross-module comment events — fired by Measure / Offer /
    // Agreement / Warehouse / Transport / Installation / Servicing comment
    // surfaces. Production has its own production.comment.* events with a
    // richer payload (article, material, responsibility) — those stay
    // separate above.
    //
    // The payload carries:
    //   entity_label    — human-readable identifier of the record being
    //                      commented on, e.g. "Marrëveshja: Projekti Y" or
    //                      "Transport #42". Built per-surface in each
    //                      controller's addComment() method.
    //   comment_excerpt — first 160 chars of the comment body with ellipsis.
    //   category_override — sets the inbox sidebar bucket (measure/offer/...)
    //                       so notifications still group correctly per module
    //                       despite sharing one event-type row in Settings.
    //
    // Same layout for both events — only the title template differs (mention
    // = "X mentioned you on Y" vs follower = "New comment on Y"); the body
    // and the drawer card are identical because the data they surface is
    // the same.
    'comment.created': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.commentedBy', 'Commented by'),
        fieldFromPayload('entity_label', 'notifications.drawer.on', 'On'),
        fieldFromPayload('comment_excerpt', 'notifications.drawer.commentExcerpt', 'Comment'),
        fieldCategory,
    ],

    'comment.mentioned': [
        fieldCreatedAt,
        fieldTriggeredBy('notifications.drawer.commentedBy', 'Commented by'),
        fieldFromPayload('entity_label', 'notifications.drawer.on', 'On'),
        fieldFromPayload('comment_excerpt', 'notifications.drawer.commentExcerpt', 'Comment'),
        fieldCategory,
    ],
};

/**
 * Fallback config for events we don't have a tailored layout for yet.
 * Still gives the user what they asked for: when, by whom, category — and
 * skips the noisy "Language" row entirely.
 */
const GENERIC_CONFIG: FieldConfig[] = [
    fieldCreatedAt,
    fieldCreatedBy,
    fieldCategory,
];

/**
 * Resolve the field config for a given notification. Falls back to the
 * generic three-row config when we don't recognise the event_type key (which
 * happens for events we haven't authored a config for yet, or for
 * locally-stored items from older app versions that don't carry the event
 * type on them).
 */
export function getDrawerFields(eventKey: string | null | undefined): FieldConfig[] {
    if (!eventKey) return GENERIC_CONFIG;
    return CARD_CONFIGS[eventKey] || GENERIC_CONFIG;
}
